import React, { useMemo, useState, useEffect } from 'react';
import { Button, Card, Col, Divider, Form, Input, InputNumber, Modal, Row, Select, Space, Typography, message, Tag } from 'antd';
import { useAuth } from '../../../hooks/useAuth';
import { getCurrentUserProjects, getCustomerList } from '../../../services/project';
import { getInventoryUnitByBarcode, getInventoryUnitDetail, getInventoryUnits } from '../../../services/inventory';
import request from '../../../utils/request';

const { Title, Text } = Typography;

const PackageEstimatorPage: React.FC = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const products = Form.useWatch('products', form);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [customers, setCustomers] = useState<Array<{ id: string; username: string; name?: string; customer_code?: string }>>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRowIndex, setImportRowIndex] = useState<number | null>(null);
  const [importBarcode, setImportBarcode] = useState('');
  const [importProjectId, setImportProjectId] = useState<string | undefined>();
  const [importUnitId, setImportUnitId] = useState<string | undefined>();
  const [importPreviewUnit, setImportPreviewUnit] = useState<any | null>(null);
  const [importSearchTouched, setImportSearchTouched] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [units, setUnits] = useState<any[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [estimateResults, setEstimateResults] = useState<Record<string, any>>({});
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const isStaffOrAdmin = user && (user.role === 'admin' || user.role === 'staff');
  const stats = useMemo(() => {
    const rows = Array.isArray(products) ? products : [];
    const isValidInt = (v: any) => Number.isFinite(Number(v)) && Number(v) >= 1 && Number.isInteger(Number(v));
    const totals = rows.reduce(
      (acc, item) => {
        const l = Number(item?.length);
        const w = Number(item?.width);
        const h = Number(item?.height);
        const weight = Number(item?.weight);
        const qty = Number(item?.quantity);
        const valid = [l, w, h, weight, qty].every(isValidInt);
        if (!valid) return acc;
        acc.recordCount += 1;
        acc.totalQty += qty;
        acc.totalVolume += l * w * h * qty;
        acc.totalWeight += weight * qty;
        return acc;
      },
      { recordCount: 0, totalQty: 0, totalVolume: 0, totalWeight: 0 }
    );
    return {
      recordCount: totals.recordCount,
      totalQty: totals.totalQty,
      totalVolume: totals.totalVolume,
      totalWeight: totals.totalWeight,
    };
  }, [products]);
  const hasEstimateResults = Object.keys(estimateResults).length > 0;
  const allEstimateUnavailable = useMemo(() => {
    if (!hasEstimateResults) return false;
    const keys = ['warehouse_free', 'warehouse_paid', 'customer_carton'];
    return keys.every((key) => !(estimateResults as any)?.[key]?.recommended_box);
  }, [estimateResults, hasEstimateResults]);
  const allRowsComplete = useMemo(() => {
    const rows = Array.isArray(products) ? products : [];
    if (rows.length === 0) return false;
    const isValidInt = (v: any) => Number.isFinite(Number(v)) && Number(v) >= 1 && Number.isInteger(Number(v));
    return rows.every((item) => {
      const l = Number(item?.length);
      const w = Number(item?.width);
      const h = Number(item?.height);
      const weight = Number(item?.weight);
      const qty = Number(item?.quantity);
      return [l, w, h, weight, qty].every(isValidInt);
    });
  }, [products]);

  useEffect(() => {
    let active = true;
    if (isStaffOrAdmin) {
      (async () => {
        try {
          setCustomersLoading(true);
          const list = await getCustomerList({ onlyMain: true });
          if (!active) return;
          setCustomers(Array.isArray(list) ? list : []);
        } catch {
          if (!active) return;
          setCustomers([]);
        } finally {
          if (active) setCustomersLoading(false);
        }
      })();
    } else if (user?.role === 'customer') {
      setCustomerId(String(user.id));
    }
    return () => { active = false; };
  }, [isStaffOrAdmin, user]);

  const validItems = useMemo(() => {
    const rows = Array.isArray(products) ? products : [];
    const isValidInt = (v: any) => Number.isFinite(Number(v)) && Number(v) >= 1 && Number.isInteger(Number(v));
    return rows
      .map((item) => {
        const length_cm = Number(item?.length);
        const width_cm = Number(item?.width);
        const height_cm = Number(item?.height);
        const weight_g = Number(item?.weight);
        const quantity = Number(item?.quantity);
        const valid = [length_cm, width_cm, height_cm, weight_g, quantity].every(isValidInt);
        return valid ? { length_cm, width_cm, height_cm, weight_g, quantity } : null;
      })
      .filter(Boolean) as Array<{ length_cm: number; width_cm: number; height_cm: number; weight_g: number; quantity: number }>;
  }, [products]);

  useEffect(() => {
    let active = true;
    if (!importModalOpen) return;
    (async () => {
      try {
        setProjectsLoading(true);
        const data = await getCurrentUserProjects();
        if (!active) return;
        setProjects(Array.isArray(data) ? data : []);
      } catch {
        if (!active) return;
        setProjects([]);
      } finally {
        if (active) setProjectsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [importModalOpen]);

  useEffect(() => {
    let active = true;
    if (!importProjectId) {
      setUnits([]);
      setImportUnitId(undefined);
      return;
    }
    (async () => {
      try {
        setUnitsLoading(true);
        const resp = await getInventoryUnits({ project: Number(importProjectId), page_size: 50 });
        const rows = Array.isArray((resp as any)?.data?.results) ? (resp as any).data.results : [];
        if (!active) return;
        setUnits(rows);
      } catch {
        if (!active) return;
        setUnits([]);
      } finally {
        if (active) setUnitsLoading(false);
      }
    })();
    return () => { active = false; };
  }, [importProjectId]);

  const resetImportState = () => {
    setImportBarcode('');
    setImportProjectId(undefined);
    setImportUnitId(undefined);
    setImportPreviewUnit(null);
    setImportSearchTouched(false);
  };

  const handleOpenImport = (rowIndex: number) => {
    setImportRowIndex(rowIndex);
    resetImportState();
    setImportModalOpen(true);
  };

  const applyImportToRow = (rowIndex: number, payload: any) => {
    const l = Number(payload?.actual_length ?? payload?.length);
    const w = Number(payload?.actual_width ?? payload?.width);
    const h = Number(payload?.actual_height ?? payload?.height);
    const weight = Number(payload?.actual_weight ?? payload?.weight);
    const code = String(payload?.system_barcode || payload?.system_code || payload?.barcode || '');
    if (!Number.isFinite(l) || !Number.isFinite(w) || !Number.isFinite(h)) {
      message.warning('该库存单元缺少有效尺寸数据');
      return;
    }
    const next = Array.isArray(products) ? [...products] : [];
    const current = next[rowIndex] || {};
    next[rowIndex] = {
      ...current,
      length: l,
      width: w,
      height: h,
      weight: Number.isFinite(weight) ? weight : current.weight,
      imported_system_barcode: code || current.imported_system_barcode,
    };
    form.setFieldsValue({ products: next });
  };

  const handleSearchBarcode = async () => {
    const keyword = importBarcode.trim();
    if (!keyword) {
      message.warning('请输入库存单元系统编码');
      return;
    }
    setImportSearchTouched(true);
    try {
      setImportLoading(true);
      const resp = await getInventoryUnitByBarcode(keyword, { customer_only: true });
      const data = (resp as any)?.data || resp;
      if (!data) {
        setImportPreviewUnit(null);
        setImportUnitId(undefined);
        return;
      }
      const code = String(data?.system_barcode || '');
      if (code && code.toLowerCase() !== keyword.toLowerCase()) {
        setImportPreviewUnit(null);
        setImportUnitId(undefined);
        return;
      }
      setImportPreviewUnit(data);
      setImportUnitId(String(data?.id || ''));
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setImportPreviewUnit(null);
        setImportUnitId(undefined);
      } else {
        message.error(err?.message || '搜索失败，请重试');
      }
    } finally {
      setImportBarcode('');
      setImportProjectId(undefined);
      setImportUnitId(undefined);
      setUnits([]);
      setImportLoading(false);
    }
  };

  const handleSelectUnit = async (val?: string) => {
    setImportUnitId(val);
    if (!val) {
      setImportPreviewUnit(null);
      setImportSearchTouched(false);
      return;
    }
    try {
      setImportLoading(true);
      const resp = await getInventoryUnitDetail(val);
      const data = (resp as any)?.data || resp;
      setImportPreviewUnit(data || null);
      setImportSearchTouched(false);
    } catch (err: any) {
      message.error(err?.message || '加载库存单元失败');
      setImportPreviewUnit(null);
    } finally {
      setImportLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (importRowIndex == null) return;
    if (!importPreviewUnit) {
      message.warning('请先搜索或选择库存单元');
      return;
    }
    try {
      setImportLoading(true);
      const data = importPreviewUnit;
      if (!data) {
        message.error('未获取到库存单元信息');
        return;
      }
      applyImportToRow(importRowIndex, data);
      setImportModalOpen(false);
    } catch (err: any) {
      message.error(err?.message || '导入失败，请重试');
    } finally {
      setImportLoading(false);
    }
  };

  const handleEstimate = async () => {
    if (validItems.length === 0) {
      message.warning('请先填写完整的产品信息');
      return;
    }
    if (isStaffOrAdmin && !customerId) {
      message.warning('请选择客户');
      return;
    }
    try {
      setEstimating(true);
      const categories = ['warehouse_free', 'warehouse_paid', 'customer_carton'];
      const responses = await Promise.all(
        categories.map((category) =>
          request.post('/api/v1/logistics/packaging-recommendation/', {
            items: validItems,
            category,
            customer_id: customerId ? Number(customerId) : undefined,
          })
        )
      );
      const nextResults: Record<string, any> = {};
      categories.forEach((category, idx) => {
        const resp = responses[idx] as any;
        nextResults[category] = resp?.data || resp;
      });
      setEstimateResults(nextResults);
    } catch (err: any) {
      message.error(err?.message || '预估失败，请重试');
    } finally {
      setEstimating(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setEstimateResults({});
  };

  const formatInt = (v: any) => {
    return Number.isFinite(Number(v)) ? String(Math.round(Number(v))) : '-';
  };

  return (
    <div className="list-page-container">
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Space direction="vertical" size={4}>
            <Space size={20}>
              <Title level={4} style={{ margin: 0 }}>包装预估</Title>
              <Button
                onClick={() => setInfoModalOpen(true)}
                style={{ borderColor: '#d9d9d9', color: '#595959' }}
              >
                详细说明
              </Button>
            </Space>
            <Text type="secondary">输入产品信息,预估仓库将使用的包材和打包后的重量及尺寸.</Text>
          </Space>
          {isStaffOrAdmin ? (
            <div style={{ minWidth: 260 }}>
              <div style={{ marginBottom: 6, color: '#666' }}>选择客户</div>
              <Select
                placeholder="请选择客户"
                value={customerId}
                onChange={(val) => setCustomerId(val)}
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
                loading={customersLoading}
                options={customers.map((c) => ({
                  value: String(c.id),
                  label: `${c.username || c.name || c.id}`,
                }))}
              />
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        style={{ marginTop: 16 }}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <Space size={8}>
              <span>需打包产品信息</span>
              <Button
                style={{ background: '#e6f4ff', borderColor: '#91caff', color: '#1677ff' }}
                onClick={() => form.setFieldsValue({ products: [...(Array.isArray(products) ? products : []), { length: null, width: null, height: null, weight: null, quantity: 1 }] })}
              >
                添加产品
              </Button>
            </Space>
            <Text type="secondary">
              已添加 有效产品记录 {Math.round(stats.recordCount)} | 产品总数量 {Math.round(stats.totalQty)} | 产品总体积 {Math.round(stats.totalVolume)} cm³ | 产品总净重 {Math.round(stats.totalWeight)} g
            </Text>
          </div>
        )}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ products: [{ length: null, width: null, height: null, weight: null, quantity: 1 }] }}
        >
          <Form.List name="products">
            {(fields, { add, remove }) => (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {fields.map((field, index) => (
                  <Row key={field.key} gutter={12} align="middle" style={{ flexWrap: 'nowrap' }}>
                    <Col flex="40px">
                      <Text type="secondary">{index + 1}</Text>
                    </Col>
                    <Col flex="120px">
                      <Form.Item {...field} label="长度 (cm)" name={[field.name, 'length']}>
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="长度" />
                      </Form.Item>
                    </Col>
                    <Col flex="120px">
                      <Form.Item {...field} label="宽度 (cm)" name={[field.name, 'width']}>
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="宽度" />
                      </Form.Item>
                    </Col>
                    <Col flex="120px">
                      <Form.Item {...field} label="高度 (cm)" name={[field.name, 'height']}>
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="高度" />
                      </Form.Item>
                    </Col>
                    <Col flex="120px">
                      <Form.Item {...field} label="重量 (g)" name={[field.name, 'weight']}>
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="重量" />
                      </Form.Item>
                    </Col>
                    <Col flex="110px">
                      <Form.Item {...field} label="数量" name={[field.name, 'quantity']}>
                        <InputNumber min={1} precision={0} step={1} style={{ width: '100%' }} placeholder="数量" />
                      </Form.Item>
                    </Col>
                    <Col flex="60px">
                      <Button
                        style={{
                          background: (Array.isArray(products) && products[index]?.imported_system_barcode) ? '#fff7e6' : '#f6ffed',
                          borderColor: (Array.isArray(products) && products[index]?.imported_system_barcode) ? '#ffd591' : '#b7eb8f',
                          color: (Array.isArray(products) && products[index]?.imported_system_barcode) ? '#d46b08' : '#389e0d',
                        }}
                        onClick={() => handleOpenImport(index)}
                      >
                        {(Array.isArray(products) && products[index]?.imported_system_barcode)
                          ? `已导入:${products[index]?.imported_system_barcode}`
                          : '从库存单元导入'}
                      </Button>
                    </Col>
                    <Col flex="60px">
                      <Button danger onClick={() => remove(field.name)} disabled={fields.length === 1}>
                        移除
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Row gutter={12}>
                  <Col flex="auto">
                    <Space>
                      <Button type="primary" onClick={handleEstimate} loading={estimating} disabled={!allRowsComplete}>
                        开始预估
                      </Button>
                      <Button onClick={handleReset} disabled={estimating}>
                        重置
                      </Button>
                    </Space>
                  </Col>
                </Row>
              </Space>
            )}
          </Form.List>
        </Form>
      </Card>

      <Card
        style={{ marginTop: 16 }}
        title={(
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <span>预估结果</span>
              {hasEstimateResults ? (
                <Text type="secondary">
                  产品总数量 {Math.round(stats.totalQty)} | 产品总体积 {Math.round(stats.totalVolume)} cm³ | 产品总净重 {Math.round(stats.totalWeight)} g
                </Text>
              ) : null}
            </div>
            {allEstimateUnavailable ? (
              <div style={{ marginTop: 6 }}>
                <Text type="danger">
                  当前无可用标准包材,请联系本司客服,可执行特殊操作,比如改箱等(将多个空箱合并成一个大箱),可能产生额外费用。
                </Text>
              </div>
            ) : null}
          </div>
        )}
      >
        <Row gutter={16}>
          <Col span={8}>
            {(() => {
              const result = estimateResults.warehouse_free || {};
              const box = result.recommended_box || null;
              const available = !!box;
              const tagStyle = available
                ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontSize: 12 }
                : { background: '#fff2f0', borderColor: '#ffccc7', color: '#cf1322', fontSize: 12 };
              return (
                <Card
                  size="small"
                  title={(
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>仓库免费纸箱</span>
                      <Tag style={tagStyle}>{available ? '可用' : '不可用'}</Tag>
                    </div>
                  )}
                  style={available ? undefined : { opacity: 0.5 }}
                >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {(() => {
                  return (
                    <>
                      <Text>推荐包材：{box ? (box.name || '-') : '-'}</Text>
                      <Text>包材编码：{box ? (box.system_code || '-') : '-'}</Text>
                      <Text>包材净重：{formatInt(box?.weight_g)} g</Text>
                      <Text>包材价格：{box?.price != null && box?.price !== '' ? `$${box?.price}` : '-'}</Text>
                      <Text>
                        预估包裹尺寸：{formatInt(result.estimated_length_cm)} × {formatInt(result.estimated_width_cm)} × {formatInt(result.estimated_height_cm)} cm
                      </Text>
                      <Text>预估包裹体积：{formatInt(Number(result.estimated_length_cm) * Number(result.estimated_width_cm) * Number(result.estimated_height_cm))} cm³</Text>
                      <Text>预估包裹重量：{formatInt(result.estimated_weight_g)} g</Text>
                    </>
                  );
                })()}
              </Space>
                </Card>
              );
            })()}
          </Col>
          <Col span={8}>
            {(() => {
              const result = estimateResults.warehouse_paid || {};
              const box = result.recommended_box || null;
              const available = !!box;
              const tagStyle = available
                ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontSize: 12 }
                : { background: '#fff2f0', borderColor: '#ffccc7', color: '#cf1322', fontSize: 12 };
              return (
                <Card
                  size="small"
                  title={(
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>仓库收费纸箱</span>
                      <Tag style={tagStyle}>{available ? '可用' : '不可用'}</Tag>
                    </div>
                  )}
                  style={available ? undefined : { opacity: 0.5 }}
                >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {(() => {
                  return (
                    <>
                      <Text>推荐包材：{box ? (box.name || '-') : '-'}</Text>
                      <Text>包材编码：{box ? (box.system_code || '-') : '-'}</Text>
                      <Text>包材净重：{formatInt(box?.weight_g)} g</Text>
                      <Text>包材价格：{box?.price != null && box?.price !== '' ? `$${box?.price}` : '-'}</Text>
                      <Text>
                        预估包裹尺寸：{formatInt(result.estimated_length_cm)} × {formatInt(result.estimated_width_cm)} × {formatInt(result.estimated_height_cm)} cm
                      </Text>
                      <Text>预估包裹体积：{formatInt(Number(result.estimated_length_cm) * Number(result.estimated_width_cm) * Number(result.estimated_height_cm))} cm³</Text>
                      <Text>预估包裹重量：{formatInt(result.estimated_weight_g)} g</Text>
                    </>
                  );
                })()}
              </Space>
                </Card>
              );
            })()}
          </Col>
          <Col span={8}>
            {(() => {
              const result = estimateResults.customer_carton || {};
              const box = result.recommended_box || null;
              const available = !!box;
              const tagStyle = available
                ? { background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontSize: 12 }
                : { background: '#fff2f0', borderColor: '#ffccc7', color: '#cf1322', fontSize: 12 };
              return (
                <Card
                  size="small"
                  title={(
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>客户自购纸箱</span>
                      <Tag style={tagStyle}>{available ? '可用' : '不可用'}</Tag>
                    </div>
                  )}
                  style={available ? undefined : { opacity: 0.5 }}
                >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {(() => {
                  return (
                    <>
                      <Text>推荐包材：{box ? (box.name || '-') : '-'}</Text>
                      <Text>包材编码：{box ? (box.system_code || '-') : '-'}</Text>
                      <Text>包材净重：{formatInt(box?.weight_g)} g</Text>
                      <Text>包材价格：{box?.price != null && box?.price !== '' ? `$${box?.price}` : '-'}</Text>
                      <Text>
                        预估包裹尺寸：{formatInt(result.estimated_length_cm)} × {formatInt(result.estimated_width_cm)} × {formatInt(result.estimated_height_cm)} cm
                      </Text>
                      <Text>预估包裹体积：{formatInt(Number(result.estimated_length_cm) * Number(result.estimated_width_cm) * Number(result.estimated_height_cm))} cm³</Text>
                      <Text>预估包裹重量：{formatInt(result.estimated_weight_g)} g</Text>
                    </>
                  );
                })()}
              </Space>
                </Card>
              );
            })()}
          </Col>
        </Row>
      </Card>

      <Modal
        title="从库存单元导入"
        open={importModalOpen}
        onCancel={() => setImportModalOpen(false)}
        onOk={handleConfirmImport}
        confirmLoading={importLoading}
        okButtonProps={{ disabled: !importPreviewUnit }}
        okText="确认"
        cancelText="取消"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Space>
              <Input
                style={{ width: 320 }}
                placeholder="请输入库存单元系统编码 如 LIU-25101-1"
                value={importBarcode}
                allowClear
                onChange={(e) => setImportBarcode(e.target.value)}
                onPressEnter={handleSearchBarcode}
              />
              <Button type="primary" onClick={handleSearchBarcode} loading={importLoading}>
                搜索
              </Button>
            </Space>
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>选择项目</div>
            <Select
              placeholder="请选择项目"
              value={importProjectId}
              onChange={(val) => {
                setImportProjectId(val);
                setImportPreviewUnit(null);
                setImportSearchTouched(false);
              }}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={projectsLoading}
              options={(projects || []).map((p: any) => ({
                value: String(p.id),
                label: `${p.project_code || p.id} - ${p.name || ''}`,
              }))}
            />
          </div>
          <div>
            <div style={{ marginBottom: 8 }}>项目所属库存单元</div>
            <Select
              placeholder="请选择库存单元"
              value={importUnitId}
              onChange={handleSelectUnit}
              allowClear
              showSearch
              optionFilterProp="label"
              style={{ width: '100%' }}
              loading={unitsLoading}
              disabled={!importProjectId}
              options={(units || []).map((u: any) => ({
                value: String(u.id),
                label: `${u.system_barcode || u.id}`,
              }))}
            />
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div>
            <div style={{ marginBottom: 8 }}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 6px',
                  borderRadius: 4,
                  border: '1px solid #b7eb8f',
                  background: '#f6ffed',
                  color: '#389e0d',
                  fontSize: 12,
                }}
              >
                搜索结果
              </span>
            </div>
            {importPreviewUnit ? (
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Text>库存单元系统编码：{importPreviewUnit?.system_barcode || '-'}</Text>
                <Text>
                  客户自定义编码：{(() => {
                    const items = importPreviewUnit?.content?.items || importPreviewUnit?.content_items || [];
                    if (!Array.isArray(items) || items.length === 0) return '-';
                    const codes = items.map((it: any) => it?.product_code || it?.customer_code || it?.custom_code).filter(Boolean);
                    return codes.length > 0 ? codes.join('；') : '-';
                  })()}
                </Text>
                <Text>
                  所含产品信息：{(() => {
                    const items = importPreviewUnit?.content?.items || importPreviewUnit?.content_items || [];
                    if (!Array.isArray(items) || items.length === 0) return '-';
                    return items
                      .map((it: any) => {
                        const name = it?.product_name || it?.name || it?.product?.name || '-';
                        const qty = it?.quantity || it?.qty || 0;
                        return `${name}×${qty}`;
                      })
                      .join('；');
                  })()}
                </Text>
                <Text>
                  分拣条码：{(() => {
                    const items = importPreviewUnit?.content?.items || importPreviewUnit?.content_items || [];
                    if (!Array.isArray(items) || items.length === 0) return '-';
                    const codes = items.flatMap((it: any) => (
                      Array.isArray(it?.sorting_codes) ? it.sorting_codes.map((c: any) => c?.code).filter(Boolean) : []
                    ));
                    return codes.length > 0 ? codes.join('；') : '-';
                  })()}
                </Text>
                <Text>
                  尺寸：{importPreviewUnit?.actual_length ?? importPreviewUnit?.length ?? '-'} × {importPreviewUnit?.actual_width ?? importPreviewUnit?.width ?? '-'} × {importPreviewUnit?.actual_height ?? importPreviewUnit?.height ?? '-'} cm
                </Text>
                <Text>
                  重量：{importPreviewUnit?.actual_weight ?? importPreviewUnit?.weight ?? '-'} g
                </Text>
              </Space>
            ) : importSearchTouched ? (
              <Text style={{ color: '#ff4d4f' }}>无搜索结果</Text>
            ) : (
              <Text type="secondary">暂无结果</Text>
            )}
          </div>
        </Space>
      </Modal>

      <Modal
        title="包装预估说明"
        open={infoModalOpen}
        onCancel={() => setInfoModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setInfoModalOpen(false)}>关闭</Button>
        ]}
        width={720}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            <Text strong>概览</Text>
            <div style={{ marginTop: 6, color: '#666' }}>
              1. 本页面主要用于客户提交一件代发指令前，对于需要打包的订单(单件无物流包装 或 一票多件)，预估仓库将会使用的包材及打包后的重量与尺寸。<br />
              2. 根据输入的产品信息，系统将调用推荐算法挑选最合适的包材，并展示预估的包装尺寸与重量。<br />
              3. 默认的推荐逻辑是:能够装下所有产品 且 体积最小的包材。<br />
              4. 客户可使用此预估结果(重量及尺寸)自行购买物流运单并在提交&quot;一件代发&quot;指令时上传此运单。<br />
              5. 此页面结果仅供参考,因为算法限制,仓库最终打包结果和此页面显示的结果可能不一致。<br />
              6. 若在提交&quot;一件代发&quot;指令前需要精确的打包数据,请联系本司客服。<br />
              7. 本页面仅显示使用&quot;箱子&quot;作为发货物流包装的结果,不显示其他软包装发货的结果,如塑胶袋/气泡信封等。<br />
              8. 若所有结果均显示&quot;不可用&quot;,表示当前无可用标准包材,请联系本司客服,可执行特殊操作,比如改箱等(将多个空箱合并成一个大箱),可能产生额外费用。
            </div>
          </div>
          <div>
            <Text strong>使用方式</Text>
            <div style={{ marginTop: 6, color: '#666' }}>
              1. 在“需打包产品信息”中逐行填写尺寸（cm）、重量（g）与数量（件）,所有数据仅支持正整数。<br />
              2. 如需导入库存单元尺寸和重量，可使用“从库存单元导入”。<br />
              3. 点击&quot;开始预估&quot;,查看各包材类别的预估结果。
            </div>
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default PackageEstimatorPage;
